import {Component, ContentChild, EventEmitter, Input, Output, TemplateRef} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {ContextMenu} from 'primeng/contextmenu';
import {Account, BusinessECommerceStore} from 'web-angular-client';
import {AccountStatusUtil} from '../../../sdk/interface/accountStatusUtil';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {RowSortValueAccessor} from '../../../sdk/util/row-sort';
import {SortableTableBase, SortDropdownOption} from '../../../sdk/component/table/sortable-table-base';

/**
 * Presentational table for ecommerce account (transaction) search results, shared by the ecommerce dashboard's recent
 * transactions card and the payment dashboard page. It renders what it is handed and nothing more: the parent owns the
 * rows, the loading and error flags, and the per-row menu items.
 *
 * <p>
 * {@link SortableTableBase} owns both sort entry points — the column headers, and the "Sort By" dropdown that
 * replaces them at 1024px and below — and decides which of the two sort modes applies. The payment dashboard page
 * leaves {@code clientSideSort} off, so a header click emits and the page re-queries; the dashboard card turns it on,
 * so the card reorders the few rows it holds rather than changing which rows it contains. This class only declares
 * which columns the accounts search can order by; the store column is derived after the query, so it is absent from
 * {@link #SORTABLE_COLUMNS} and cannot be sorted.
 *
 * <p>
 * The body renders exactly one of four states, in precedence order: loading, error, the rows, then {@code emptyText}.
 * A parent may project an {@code #expandedRow} template to add a detail row beneath any row it reports as expanded.
 */
@Component({
    selector: 'app-ecommerce-account-table',
    templateUrl: './ecommerce-account-table.component.html',
    standalone: false
})
export class EcommerceAccountTableComponent extends SortableTableBase<Account>
{
    @Input() accounts: Account[] | null | undefined = [];
    @Input() loading = false;
    @Input() error = false;
    @Input() emptyText = 'No transactions for the selected filters.';
    @Input() loadingText = 'Loading…';
    @Input() errorText = 'Data unavailable';
    @Input() storesById: Map<number, BusinessECommerceStore> = new Map();
    @Input() menuItemsForAccount: (account: Account) => MenuItem[] = () => [];
    @Input() clickable = false;
    @Input() isExpanded: (account: Account) => boolean = () => false;
    @Output() readonly accountSelect = new EventEmitter<Account>();
    @Output() readonly rowClick = new EventEmitter<Account>();

    @ContentChild('expandedRow', {static: false}) expandedRowTemplate: TemplateRef<{
        $implicit: Account
    }> | null = null;

    activeMenuItems: MenuItem[] = [];

    /**
     * Sort value per column, for {@code clientSideSort} only. Amount delegates to {@link #getAmount} so it cannot
     * disagree with the rendered cell. Dates are ISO strings, which compare correctly as text.
     */
    protected readonly sortValues: Record<string, RowSortValueAccessor<Account>> = {
        dateTimeCreated: account => account?.dateTimeCreated,
        accountNumber: account => account?.accountNumber,
        referenceNumber: account => account?.referenceNumber,
        customer: account => account?.customer,
        amount: account => this.getAmount(account),
        dateTimePaid: account => account?.dateTimePaid
    };

    private static readonly SORTABLE_COLUMNS: ReadonlyArray<SortDropdownOption> = [
        {id: 'dateTimeCreated', name: 'Date Created'},
        {id: 'accountNumber', name: 'Account Number'},
        {id: 'referenceNumber', name: 'Reference'},
        {id: 'customer', name: 'Name'},
        {id: 'amount', name: 'Amount'},
        {id: 'dateTimePaid', name: 'Date Paid'}
    ];

    private activeContextMenu: ContextMenu | null = null;

    constructor(
        private readonly accountStatusUtil: AccountStatusUtil,
        private readonly authenticationService: AuthenticationService)
    {
        super();
    }

    protected get rows(): Account[] | null | undefined
    {
        return this.accounts;
    }

    protected get sortableColumnOptions(): ReadonlyArray<SortDropdownOption>
    {
        return EcommerceAccountTableComponent.SORTABLE_COLUMNS;
    }

    onRowClick(account: Account): void
    {
        this.rowClick.emit(account);
    }

    openMenu(event: MouseEvent, contextMenu: ContextMenu, account: Account): void
    {
        event.stopPropagation();
        event.preventDefault();
        this.activeContextMenu?.hide();
        this.activeContextMenu = null;

        if (this.authenticationService.isGuestAdminUser())
        {
            return;
        }

        this.activeMenuItems = this.menuItemsForAccount(account);
        this.accountSelect.emit(account);
        contextMenu.show(event);
        this.activeContextMenu = contextMenu;
    }

    isGuestAdminUser(): boolean
    {
        return this.authenticationService.isGuestAdminUser();
    }

    getAmount(account: Account): number
    {
        return account?.dateTimePaid ? account.amountPaid : account.amount;
    }

    getOrderNumber(account: Account): string
    {
        return account?.orderNumber || account?.description || '-';
    }

    getStoreLabel(account: Account): string
    {
        if (!account)
        {
            return '-';
        }
        const storeId = account.eCommerceStoreId ?? null;
        if (storeId !== null)
        {
            const matched = this.storesById.get(storeId);
            if (matched?.eCommerceStoreName)
            {
                return matched.eCommerceStoreName;
            }
        }
        return account.eCommerceStoreName ?? (storeId !== null ? `${storeId}` : '-');
    }

    getLatestStatusLabel(account: Account): string
    {
        const accountState = account?.accountState;
        return accountState ? this.accountStatusUtil.getSimplifiedDescription(accountState) : 'UNKNOWN';
    }

    getLatestStatusClass(account: Account): string
    {
        return this.accountStatusUtil.getAccountTextColorClass(account?.accountState);
    }

}
